import { useEffect } from 'react';
import { Form, Input, Modal, Typography } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';

import {
    HEAT_COEFFICIENT_INPUT_PATTERN,
    HEAT_COEFFICIENT_MAX,
    HEAT_COEFFICIENT_MIN,
    formatHeatCoefficientInput,
    normalizeHeatCoefficientValue,
    sanitizeHeatCoefficientInput,
} from '@/pages/drama-review/utils';

const NAV_KEYS = new Set(['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight', 'Home', 'End']);

/** 按键后是否会被 sanitize 截断（例如第三位小数） */
const isHeatCoefficientKeyRejected = (current: string, key: string, start: number, end: number) => {
    const rawNext = `${current.slice(0, start)}${key}${current.slice(end)}`;
    const onlyLegal = rawNext.replace(/[^\d.]/g, '');
    const firstDot = onlyLegal.indexOf('.');
    const legalOneDot =
        firstDot === -1
            ? onlyLegal
            : onlyLegal.slice(0, firstDot + 1) + onlyLegal.slice(firstDot + 1).replace(/\./g, '');

    return sanitizeHeatCoefficientInput(rawNext) !== legalOneDot;
};

type Props = {
    open: boolean;
    title: React.ReactNode;
    initialValue?: number | null;
    loading?: boolean;
    onCancel: () => void;
    onSubmit: (values: { heat_coefficient: number }) => void;
};

const HeatCoefficientModal: React.FC<Props> = ({
    open,
    title,
    initialValue,
    loading,
    onCancel,
    onSubmit,
}) => {
    const [form] = Form.useForm<{ heat_coefficient: string }>();
    const { formatMessage } = useIntl();

    useEffect(() => {
        if (open) {
            form.setFieldsValue({
                heat_coefficient: formatHeatCoefficientInput(initialValue),
            });
        }
    }, [form, initialValue, open]);

    const handleFinish = ({ heat_coefficient }: { heat_coefficient: string }) => {
        onSubmit({ heat_coefficient: normalizeHeatCoefficientValue(heat_coefficient) });
    };

    return (
        <Modal
            open={open}
            title={title}
            destroyOnClose
            confirmLoading={loading}
            okText={formatMessage({ id: 'dramaReview.heatCoefficient.submit' })}
            cancelText={formatMessage({ id: 'common.cancel' })}
            onCancel={onCancel}
            onOk={() => form.submit()}
        >
            <Form form={form} layout="vertical" onFinish={handleFinish}>
                <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
                    <FormattedMessage id="dramaReview.heatCoefficient.hint" />
                </Typography.Paragraph>
                <Form.Item
                    name="heat_coefficient"
                    label={<FormattedMessage id="dramaReview.heatCoefficient.label" />}
                    getValueFromEvent={(e) => sanitizeHeatCoefficientInput(String(e?.target?.value ?? ''))}
                    rules={[
                        {
                            required: true,
                            message: formatMessage({ id: 'dramaReview.heatCoefficient.required' }),
                        },
                        {
                            validator: async (_, value: string) => {
                                if (value == null || value === '') return;

                                if (!HEAT_COEFFICIENT_INPUT_PATTERN.test(value) || value.endsWith('.')) {
                                    throw new Error(
                                        formatMessage({ id: 'dramaReview.heatCoefficient.decimals' }),
                                    );
                                }

                                const num = Number(value);
                                if (
                                    Number.isNaN(num) ||
                                    num < HEAT_COEFFICIENT_MIN ||
                                    num > HEAT_COEFFICIENT_MAX
                                ) {
                                    throw new Error(
                                        formatMessage(
                                            { id: 'dramaReview.heatCoefficient.range' },
                                            { min: HEAT_COEFFICIENT_MIN, max: HEAT_COEFFICIENT_MAX },
                                        ),
                                    );
                                }
                            },
                        },
                    ]}
                >
                    <Input
                        allowClear
                        inputMode="decimal"
                        placeholder={formatMessage({ id: 'dramaReview.heatCoefficient.placeholder' })}
                        onKeyDown={(e) => {
                            if (e.ctrlKey || e.metaKey || e.altKey) return;
                            if (NAV_KEYS.has(e.key)) return;

                            const input = e.currentTarget;
                            const start = input.selectionStart ?? input.value.length;
                            const end = input.selectionEnd ?? input.value.length;

                            if (e.key === '.' || e.key === 'Decimal') {
                                if (input.value.includes('.') && start === end) {
                                    e.preventDefault();
                                }
                                return;
                            }

                            if (/^\d$/.test(e.key)) {
                                if (isHeatCoefficientKeyRejected(input.value, e.key, start, end)) {
                                    e.preventDefault();
                                }
                                return;
                            }

                            e.preventDefault();
                        }}
                    />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default HeatCoefficientModal;
